import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Alert,
  Icon,
  alpha,
  useTheme,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import moment from "moment";
import { LedgerSettings } from "../../../mock.data";
import { useConfiguration } from "../../../components/ConfigurationContext";

export const LedgerSettingsConfig: React.FC = () => {
  const theme = useTheme();
  const { ledgerSettings, updateLedgerSettings } = useConfiguration();
  const [settings, setSettings] = useState<LedgerSettings>(ledgerSettings);

  const handleToggleChange = (field: keyof LedgerSettings) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
      updated_at: moment().valueOf(),
    }));
  };

  const handleSelectChange = (field: keyof LedgerSettings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
      updated_at: moment().valueOf(),
    }));
  };

  const handleArrayChange = (field: keyof LedgerSettings, value: string[]) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
      updated_at: moment().valueOf(),
    }));
  };

  const handleSaveSettings = () => {
    console.log("Saving ledger settings:", settings);
    updateLedgerSettings(settings);
    // Here you would typically save to backend
  };

  const handleResetToDefaults = () => {
    setSettings(ledgerSettings);
  };

  // Configuration options
  const accountDisplayFormats = [
    {
      value: "[Code] - [Name]",
      label: "[Code] - [Name]",
      example: "1001 - Cash on Hand",
    },
    { value: "[Name] only", label: "[Name] only", example: "Cash on Hand" },
    {
      value: "[Name] (Code)",
      label: "[Name] (Code)",
      example: "Cash on Hand (1001)",
    },
  ];

  const currencyHandlingModes = [
    {
      value: "base_currency_only",
      label: "Base Currency Only",
      description: "Show only BHD amounts",
    },
    {
      value: "multi_currency_with_conversion",
      label: "Multi-Currency with Conversion",
      description: "Show original + converted amounts",
    },
  ];

  const dateRangeOptions = [
    {
      value: "this_month",
      label: "This Month",
      description: "Current calendar month",
    },
    {
      value: "last_30_days",
      label: "Last 30 Days",
      description: "Rolling 30-day period",
    },
    {
      value: "this_quarter",
      label: "This Quarter",
      description: "Current fiscal quarter",
    },
    {
      value: "this_year",
      label: "This Year",
      description: "Current fiscal year",
    },
  ];

  const availableRoles = [
    "Admin",
    "Finance Manager",
    "Accountant",
    "POS Manager",
    "Data Entry",
  ];

  const getFeatureImpact = (feature: string) => {
    const impacts = {
      show_only_postable_accounts:
        "Hides header/non-postable COA accounts in ledger view to reduce clutter",
      hide_zero_balance_accounts:
        "Prevents display of accounts with no movement and zero balance",
      show_archived_accounts:
        "Allows inclusion of disabled/inactive accounts in ledger reports",
      enable_opening_balances:
        "Uses system-defined opening balance logic per fiscal year",
      balance_carry_forward_across_years:
        "Balances persist across fiscal periods without resetting",
      auto_close_nominal_accounts:
        "P&L accounts are reset to 0 at year-end with closing transfer logic",
      include_draft_journals_in_ledger:
        "Draft journals appear in ledger before being posted",
      allow_filtering_by_branch:
        "Users can filter ledger entries by specific branches",
      allow_filtering_by_department:
        "Supports segment reporting by department/cost center",
      enable_journal_drill_down:
        "Clicking on entries opens the source journal for detailed view",
      enable_source_module_tags:
        "Shows POS, Payroll, Purchase, etc. as tags per line",
      enable_view_source_document_link:
        "Adds links to Order, Invoice, Payslip, etc. if referenced",
      allow_export_to_excel:
        "Enables Excel export for data analysis and manipulation",
      allow_export_to_pdf:
        "Enables PDF formatting with branding for sharing and archiving",
      allow_export_to_csv:
        "Enables raw data export for integration with other systems",
      show_report_footer_summary:
        "Adds closing balance summary per account in reports",
      include_applied_filters_in_export:
        "Adds metadata to report header showing applied filters",
    };
    return impacts[feature as keyof typeof impacts] || "";
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 0.5,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
          >
            Ledger Settings Configuration
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", fontSize: "1rem" }}
          >
            Configure ledger display, behavior, access controls, and reporting
            options
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={handleResetToDefaults}>
            Reset to Defaults
          </Button>
          <Button variant="contained" onClick={handleSaveSettings}>
            Save Settings
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Grid container spacing={3}>
          {/* Section 1: Account Visibility Rules */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "primary.main" }}>visibility</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 1: Account Visibility Rules
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Control what appears in ledger results table
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.show_only_postable_accounts}
                              onChange={() =>
                                handleToggleChange(
                                  "show_only_postable_accounts"
                                )
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Show Only Postable Accounts
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Hide header/non-postable COA accounts
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("show_only_postable_accounts")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.hide_zero_balance_accounts}
                              onChange={() =>
                                handleToggleChange("hide_zero_balance_accounts")
                              }
                              color="warning"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Hide Zero-Balance Accounts
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Skip accounts with no movement and 0 balance
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="warning"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("hide_zero_balance_accounts")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.show_archived_accounts}
                              onChange={() =>
                                handleToggleChange("show_archived_accounts")
                              }
                              color="error"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Show Archived Accounts
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Include disabled/inactive accounts
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="error"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("show_archived_accounts")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Account Display Format
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={settings.account_display_format}
                            onChange={(e) =>
                              handleSelectChange(
                                "account_display_format",
                                e.target.value
                              )
                            }
                          >
                            {accountDisplayFormats.map((format) => (
                              <MenuItem key={format.value} value={format.value}>
                                <Box>
                                  <Typography variant="body2">
                                    {format.label}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    Example: {format.example}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 2: Balance Calculation Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "success.main" }}>calculate</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 2: Balance Calculation Settings
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Critical to financial period management
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.enable_opening_balances}
                              onChange={() =>
                                handleToggleChange("enable_opening_balances")
                              }
                              color="success"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Enable Opening Balances
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Use system-defined opening balance logic
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="success"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("enable_opening_balances")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                settings.balance_carry_forward_across_years
                              }
                              onChange={() =>
                                handleToggleChange(
                                  "balance_carry_forward_across_years"
                                )
                              }
                              color="info"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Balance Carry Forward Across Years
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Balances persist across fiscal periods
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact(
                            "balance_carry_forward_across_years"
                          )}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.auto_close_nominal_accounts}
                              onChange={() =>
                                handleToggleChange(
                                  "auto_close_nominal_accounts"
                                )
                              }
                              color="warning"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Auto-Close Nominal Accounts
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                P&L accounts reset to 0 at year-end
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="warning"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("auto_close_nominal_accounts")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Currency Handling Mode
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={settings.currency_handling_mode}
                            onChange={(e) =>
                              handleSelectChange(
                                "currency_handling_mode",
                                e.target.value
                              )
                            }
                          >
                            {currencyHandlingModes.map((mode) => (
                              <MenuItem key={mode.value} value={mode.value}>
                                <Box>
                                  <Typography variant="body2">
                                    {mode.label}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {mode.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                settings.include_draft_journals_in_ledger
                              }
                              onChange={() =>
                                handleToggleChange(
                                  "include_draft_journals_in_ledger"
                                )
                              }
                              color="error"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Include Draft Journals in Ledger
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Show journals that are saved but not posted
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="error"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("include_draft_journals_in_ledger")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 3: Filters & User Access Controls */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "warning.main" }}>security</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 3: Filters & User Access Controls
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Provides data confidentiality in shared environments
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allow_filtering_by_branch}
                              onChange={() =>
                                handleToggleChange("allow_filtering_by_branch")
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Allow Filtering by Branch
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Users can filter by specific branches
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("allow_filtering_by_branch")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allow_filtering_by_department}
                              onChange={() =>
                                handleToggleChange(
                                  "allow_filtering_by_department"
                                )
                              }
                              color="secondary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Allow Filtering by Department/Cost Center
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Supports segment reporting
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("allow_filtering_by_department")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Role-Based Ledger Access
                        </Typography>
                        <Autocomplete
                          multiple
                          options={availableRoles}
                          value={settings.role_based_ledger_access}
                          onChange={(_, newValue) =>
                            handleArrayChange(
                              "role_based_ledger_access",
                              newValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Select roles who can view full ledger"
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                label={option}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Default Date Range
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={settings.default_date_range}
                            onChange={(e) =>
                              handleSelectChange(
                                "default_date_range",
                                e.target.value
                              )
                            }
                          >
                            {dateRangeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box>
                                  <Typography variant="body2">
                                    {option.label}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {option.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 4: Drill-Down Behavior */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "info.main" }}>link</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 4: Drill-Down Behavior
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Makes the ledger deeply traceable, not just readable
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.enable_journal_drill_down}
                              onChange={() =>
                                handleToggleChange("enable_journal_drill_down")
                              }
                              color="info"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Enable Journal Drill-Down
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Click entries to open source journal
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("enable_journal_drill_down")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.enable_source_module_tags}
                              onChange={() =>
                                handleToggleChange("enable_source_module_tags")
                              }
                              color="success"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Enable Source Module Tags
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Show POS, Payroll, Purchase tags
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="success"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("enable_source_module_tags")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                settings.enable_view_source_document_link
                              }
                              onChange={() =>
                                handleToggleChange(
                                  "enable_view_source_document_link"
                                )
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Enable View Source Document Link
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Links to Order, Invoice, Payslip, etc.
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("enable_view_source_document_link")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Section 5: Export & Reporting Controls */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: "error.main" }}>file_download</Icon>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Section 5: Export & Reporting Controls
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Ensures exports match filtered views and maintain audit
                      quality
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allow_export_to_excel}
                              onChange={() =>
                                handleToggleChange("allow_export_to_excel")
                              }
                              color="success"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Allow Export to Excel
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Enable "Export to XLSX" button
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="success"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("allow_export_to_excel")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allow_export_to_pdf}
                              onChange={() =>
                                handleToggleChange("allow_export_to_pdf")
                              }
                              color="error"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Allow Export to PDF
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Enable PDF formatting with branding
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="error"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("allow_export_to_pdf")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.allow_export_to_csv}
                              onChange={() =>
                                handleToggleChange("allow_export_to_csv")
                              }
                              color="warning"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Allow Export to CSV
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Enable raw data export
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="warning"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("allow_export_to_csv")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.show_report_footer_summary}
                              onChange={() =>
                                handleToggleChange("show_report_footer_summary")
                              }
                              color="info"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Show Report Footer Summary
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Add closing balance summary per account
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact("show_report_footer_summary")}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                settings.include_applied_filters_in_export
                              }
                              onChange={() =>
                                handleToggleChange(
                                  "include_applied_filters_in_export"
                                )
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Include Applied Filters in Export
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Add metadata to report header
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", m: 0 }}
                        />
                        <Alert
                          severity="info"
                          sx={{ mt: 2, fontSize: "0.75rem" }}
                        >
                          {getFeatureImpact(
                            "include_applied_filters_in_export"
                          )}
                        </Alert>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Configuration Summary */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Configuration Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Icon sx={{ fontSize: 32, color: "primary.main", mb: 1 }}>
                        {settings.show_only_postable_accounts
                          ? "visibility"
                          : "visibility_off"}
                      </Icon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Account Visibility
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {settings.show_only_postable_accounts
                          ? "Postable Only"
                          : "All Accounts"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Icon sx={{ fontSize: 32, color: "success.main", mb: 1 }}>
                        {settings.enable_opening_balances
                          ? "account_balance"
                          : "money_off"}
                      </Icon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Opening Balances
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {settings.enable_opening_balances
                          ? "Enabled"
                          : "Disabled"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Icon sx={{ fontSize: 32, color: "warning.main", mb: 1 }}>
                        {settings.allow_filtering_by_branch
                          ? "filter_list"
                          : "filter_list_off"}
                      </Icon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Branch Filter
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {settings.allow_filtering_by_branch
                          ? "Available"
                          : "Hidden"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Icon sx={{ fontSize: 32, color: "info.main", mb: 1 }}>
                        {settings.enable_journal_drill_down
                          ? "link"
                          : "link_off"}
                      </Icon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Drill-Down
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {settings.enable_journal_drill_down
                          ? "Enabled"
                          : "Disabled"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Icon sx={{ fontSize: 32, color: "error.main", mb: 1 }}>
                        file_download
                      </Icon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Export Formats
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {
                          [
                            settings.allow_export_to_excel,
                            settings.allow_export_to_pdf,
                            settings.allow_export_to_csv,
                          ].filter(Boolean).length
                        }{" "}
                        Enabled
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ textAlign: "center", p: 2 }}>
                      <Icon
                        sx={{ fontSize: 32, color: "secondary.main", mb: 1 }}
                      >
                        security
                      </Icon>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Access Control
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {settings.role_based_ledger_access.length} Roles
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
